/**
 * Gesture-driven motion, in Apple's terms. A spring animates from wherever the
 * value is now and can be retargeted or grabbed at any frame, which is what
 * makes a drag feel like it continues at the finger's speed instead of
 * restarting. Two parameters: response, the seconds to reach the target, and
 * damping ratio, where 1 is no overshoot. The pure maths are exported so they
 * can be tested without a browser.
 */

export interface SpringState {
  value: number;
  velocity: number;
}

const SUBSTEP = 1 / 240;
const MAX_FRAME = 0.064;

/** Advances a damped spring by `dt` seconds in small fixed substeps so it stays stable at any frame rate. */
export function integrate(
  state: SpringState,
  target: number,
  response: number,
  damping: number,
  dt: number,
): SpringState {
  const stiffness = ((2 * Math.PI) / response) ** 2;
  const friction = 2 * damping * Math.sqrt(stiffness);
  let { value, velocity } = state;
  let remaining = Math.min(MAX_FRAME, dt);
  while (remaining > 0) {
    const step = Math.min(SUBSTEP, remaining);
    remaining -= step;
    const acceleration = -stiffness * (value - target) - friction * velocity;
    velocity += acceleration * step;
    value += velocity * step;
  }
  return { value, velocity };
}

/** Where a flick would come to rest on its own. Apple's projection, not the textbook one. */
export function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/** Progressive resistance past a boundary. The further past, the less the element follows. */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

export function nearest(x: number, points: readonly number[]): number {
  let best = points[0] ?? x;
  for (const p of points) if (Math.abs(p - x) < Math.abs(best - x)) best = p;
  return best;
}

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface SpringOptions {
  response?: number;
  damping?: number;
  /** How close to the target counts as arrived, in the value's units. */
  precision?: number;
  restVelocity?: number;
  /** Jump to every target instead of animating. Defaults to the reduced-motion setting. */
  immediate?: boolean;
  onUpdate?: (value: number) => void;
  onRest?: () => void;
}

export interface Retarget {
  velocity?: number;
  response?: number;
  damping?: number;
}

export class Spring {
  value: number;
  velocity = 0;
  target: number;
  response: number;
  damping: number;
  private readonly precision: number;
  private readonly restVelocity: number;
  private readonly immediate: boolean;
  private readonly onUpdate: ((value: number) => void) | undefined;
  private readonly onRest: (() => void) | undefined;
  private frame = 0;
  private last = 0;

  constructor(value: number, options: SpringOptions = {}) {
    this.value = value;
    this.target = value;
    this.response = options.response ?? 0.4;
    this.damping = options.damping ?? 1;
    this.precision = options.precision ?? 0.05;
    this.restVelocity = options.restVelocity ?? 0.5;
    this.immediate = options.immediate ?? prefersReducedMotion();
    this.onUpdate = options.onUpdate;
    this.onRest = options.onRest;
  }

  get running(): boolean {
    return this.frame !== 0;
  }

  /** Aims at a new target from the current value. Velocity carries over unless replaced. */
  set(target: number, retarget: Retarget = {}): this {
    this.target = target;
    if (retarget.velocity !== undefined) this.velocity = retarget.velocity;
    if (retarget.response !== undefined) this.response = retarget.response;
    if (retarget.damping !== undefined) this.damping = retarget.damping;
    if (this.immediate) {
      this.snap(target);
      return this;
    }
    if (this.frame === 0) {
      this.last = performance.now();
      this.frame = requestAnimationFrame((now) => this.step(now));
    }
    return this;
  }

  /** Jumps straight to a value. */
  snap(value: number): void {
    this.stop();
    this.value = value;
    this.target = value;
    this.velocity = 0;
    this.onUpdate?.(value);
    this.onRest?.();
  }

  /** Freezes where it is, keeping the value so a grab can continue from it. */
  stop(): void {
    if (this.frame !== 0) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  /** Sets the presentation value directly, for the frames where the finger is in charge. */
  hold(value: number): void {
    this.stop();
    this.value = value;
    this.velocity = 0;
    this.onUpdate?.(value);
  }

  private step(now: number): void {
    const dt = (now - this.last) / 1000;
    this.last = now;
    const next = integrate(this, this.target, this.response, this.damping, dt);
    this.value = next.value;
    this.velocity = next.velocity;
    const done = Math.abs(this.velocity) < this.restVelocity && Math.abs(this.value - this.target) < this.precision;
    if (done) {
      this.value = this.target;
      this.velocity = 0;
    }
    this.onUpdate?.(this.value);
    if (done) {
      this.frame = 0;
      this.onRest?.();
    } else {
      this.frame = requestAnimationFrame((t) => this.step(t));
    }
  }
}

export interface DragOptions {
  axis?: "x" | "y";
  /** Movement before a drag commits, so a tap and a scroll in the other direction stay themselves. */
  threshold?: number;
  onStart?: () => void;
  /** Distance from where the drag committed, along the axis. */
  onMove?: (delta: number) => void;
  /** Release velocity along the axis in px/s, measured over the last 100ms. */
  onEnd?: (velocity: number) => void;
  onTap?: (event: PointerEvent) => void;
}

const HISTORY_MS = 100;

/**
 * 1:1 tracking with pointer capture. Controls inside the surface keep their own
 * clicks. Returns a function that removes the listeners.
 */
export function drag(el: HTMLElement, options: DragOptions): () => void {
  const axis = options.axis ?? "y";
  const threshold = options.threshold ?? 8;
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let committed = false;
  let cancelled = false;
  let history: Array<[time: number, position: number]> = [];
  const along = (dx: number, dy: number): number => (axis === "y" ? dy : dx);

  const down = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest("button, a, input, textarea, select")) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    committed = false;
    cancelled = false;
    history = [];
    el.setPointerCapture(pointerId);
  };

  const move = (event: PointerEvent): void => {
    if (event.pointerId !== pointerId || cancelled) return;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (!committed) {
      if (Math.hypot(dx, dy) < threshold) return;
      const across = Math.abs(axis === "y" ? dx : dy);
      if (across > Math.abs(along(dx, dy))) {
        cancelled = true;
        el.releasePointerCapture(pointerId);
        pointerId = null;
        return;
      }
      committed = true;
      options.onStart?.();
    }
    const position = along(dx, dy);
    const now = performance.now();
    history.push([now, position]);
    while (history.length > 0 && now - (history[0]?.[0] ?? now) > HISTORY_MS) history.shift();
    if (event.cancelable) event.preventDefault();
    options.onMove?.(position);
  };

  const up = (event: PointerEvent): void => {
    if (event.pointerId !== pointerId) return;
    el.releasePointerCapture(pointerId);
    pointerId = null;
    if (!committed) {
      if (!cancelled) options.onTap?.(event);
      return;
    }
    let velocity = 0;
    const first = history[0];
    const last = history[history.length - 1];
    if (first && last && last[0] > first[0]) velocity = ((last[1] - first[1]) / (last[0] - first[0])) * 1000;
    options.onEnd?.(velocity);
  };

  el.addEventListener("pointerdown", down);
  el.addEventListener("pointermove", move);
  el.addEventListener("pointerup", up);
  el.addEventListener("pointercancel", up);
  return () => {
    el.removeEventListener("pointerdown", down);
    el.removeEventListener("pointermove", move);
    el.removeEventListener("pointerup", up);
    el.removeEventListener("pointercancel", up);
  };
}
